import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import {
  HealthCondition,
  HealthConditionResponse,
  HealthConditionType,
  MedicalRecordResponse,
} from '../interfaces/health-condition.interface';

@Injectable({
  providedIn: 'root',
})
export class MedicalRecordService extends BaseService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Récupère les conditions de santé (comorbidités, antécédents, etc.) pour un passeport donné
   * @param passportNumber - Le numéro de passeport
   * @param type - Type de condition : 'comorbidity' | 'precedent' | 'all'
   * @param page - Page actuelle (optionnel, défaut: 1)
   * @param limit - Nombre d'éléments par page (optionnel, défaut: 10)
   * @param orderBy - Tri (optionnel, ex: 'clinicalStatus')
   * @returns Observable contenant la réponse avec les conditions et la pagination
   */
  getHealthConditions(
    passportNumber: string,
    type: HealthConditionType,
    page: number = 1,
    limit: number = 10,
    orderBy?: string
  ): Observable<HealthConditionResponse> {
    const params = this.createParams({
      passportNumber,
      type,
      page,
      limit,
      ...(orderBy && { orderBy }),
    });

    return this.http.get<HealthConditionResponse>(
      `${this.baseUrl}/medical-records/health-conditions`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  /**
   * Récupère le dossier médical (comorbidités, antécédents, habitudes, etc.)
   * @param passportNumber - Le numéro de passeport
   * @returns Observable contenant comorbidities, antecedents, habits, gynecologicalHistory
   */
  getMedicalRecord(passportNumber: string): Observable<MedicalRecordResponse> {
    const params = this.createParams({ passportNumber });
    return this.http.get<MedicalRecordResponse>(
      `${this.baseUrl}/medical-records`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  /**
   * Récupère l'historique des versions d'une condition de santé
   * @param conditionId - L'ID de la condition (_id)
   * @returns Observable contenant la liste des versions (historique)
   */
  getHealthConditionHistory(conditionId: string): Observable<HealthCondition[]> {
    return this.http.get<HealthCondition[]>(
      `${this.baseUrl}/medical-records/health-conditions/${conditionId}/versions`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  getHabitHistory(
    passportNumber: string,
    code: string
  ): Observable<any[]> {
    const params = this.createParams({ passportNumber });
    return this.http.get<any[]>(
      `${this.baseUrl}/medical-records/habits/${code}/versions`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  getGynecologicalHistoryVersions(
    passportNumber: string
  ): Observable<any[]> {
    const params = this.createParams({ passportNumber });
    return this.http.get<any[]>(
      `${this.baseUrl}/medical-records/gynecological-history/versions`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  getFamilyHistoryVersions(
    passportNumber: string
  ): Observable<any[]> {
    const params = this.createParams({ passportNumber });
    return this.http.get<any[]>(
      `${this.baseUrl}/medical-records/family-history/versions`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  /**
   * Crée une condition de santé (comorbidité ou antécédent)
   * @param passportNumber - Numéro de passeport
   * @param type - 'comorbidity' | 'precedent'
   * @param code - Code de la condition
   * @param label - Libellé
   * @param text - Commentaire (optionnel)
   */
  createHealthCondition(
    passportNumber: string,
    type: 'comorbidity' | 'precedent',
    code: string,
    label: string,
    text?: string
  ): Observable<HealthCondition> {
    return this.http.post<HealthCondition>(
      `${this.baseUrl}/medical-records/health-conditions`,
      { passportNumber, type, code, label, text: text ?? '' },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Crée et met à jour les conditions de santé en une seule requête
   * conditions: créations (type, code, label, text) et mises à jour (_id, clinicalStatus, isCurrent)
   */
  createHealthConditionsBatch(
    passportNumber: string,
    conditions: Array<{
      type?: 'comorbidity' | 'precedent';
      code?: string;
      label?: string;
      text?: string;
      _id?: string;
      clinicalStatus?: string;
      isCurrent?: boolean;
    }>
  ): Observable<HealthCondition[]> {
    return this.http.post<HealthCondition[]>(
      `${this.baseUrl}/medical-records/health-conditions`,
      { passportNumber, conditions },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Crée et met à jour les habitudes de vie en une seule requête
   * habits: créations ou mises à jour (type, code, label, valueString, etc.)
   */
  createHabitsBatch(
    passportNumber: string,
    habits: Array<{
      code?: string;
      label?: string;
      category?: string;
      valueString?: string;
      note?: string;
      sevrage?: boolean;
      sevrageNote?: string;
    }>
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/medical-records/habits`,
      { passportNumber, habits },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Crée ou met à jour le dossier gynéco-obstétrique
   */
  createOrUpdateGynecologicalHistory(
    data: any
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/medical-records/gynecological-history`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Crée ou met à jour le dossier familial
   */
  createOrUpdateFamilyHistory(
    data: any
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/medical-records/family-history`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Remet une condition à l'état "Non fait" (soft-delete de toutes ses versions).
   * @param conditionId - L'_id MongoDB de la condition
   */
  deleteHealthCondition(conditionId: string): Observable<{ affected: number }> {
    return this.http.delete<{ affected: number }>(
      `${this.baseUrl}/medical-records/health-conditions/${conditionId}`,
      { headers: this.getAuthHeaders() }
    );
  }
}
